import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	getCommonStands,
	getMillSetupSheet,
	getProductMillSetup,
	getProductSizes,
	getProductTypes,
	getRollsByStand,
	upsertMillSetupSheet,
} from "../repositories/MillSetupSheetRepository";

export const MILL_SETUP_SHEET_QUERY_KEYS = {
	all: ["millSetupSheet"] as const,
	commonStands: ["millSetupSheet", "commonStands"] as const,
	productTypes: ["millSetupSheet", "productTypes"] as const,
	productSizes: (productTypeId: number | null) =>
		["millSetupSheet", "productSizes", productTypeId] as const,
	sheet: (productSizeId: number | null) =>
		["millSetupSheet", "sheet", productSizeId] as const,
	standSetup: (productSizeId: number | null) =>
		["millSetupSheet", "standSetup", productSizeId] as const,
	rollsByStand: (productSizeId: number | null) =>
		["millSetupSheet", "rollsByStand", productSizeId] as const,
};

export const useCommonStands = () =>
	useQuery({
		queryKey: MILL_SETUP_SHEET_QUERY_KEYS.commonStands,
		queryFn: getCommonStands,
		staleTime: 5 * 60 * 1000,
	});

export const useProductTypes = () =>
	useQuery({
		queryKey: MILL_SETUP_SHEET_QUERY_KEYS.productTypes,
		queryFn: getProductTypes,
		staleTime: 5 * 60 * 1000,
	});

export const useProductSizes = (productTypeId: number | null) =>
	useQuery({
		queryKey: MILL_SETUP_SHEET_QUERY_KEYS.productSizes(productTypeId),
		queryFn: () => getProductSizes(productTypeId),
		enabled: productTypeId != null,
		staleTime: 5 * 60 * 1000,
	});

export const useMillSetupSheet = (productSizeId: number | null) =>
	useQuery({
		queryKey: MILL_SETUP_SHEET_QUERY_KEYS.sheet(productSizeId),
		queryFn: () => getMillSetupSheet(productSizeId!),
		enabled: productSizeId != null,
	});

export const useStandSetup = (productSizeId: number | null) =>
	useQuery({
		queryKey: MILL_SETUP_SHEET_QUERY_KEYS.standSetup(productSizeId),
		queryFn: () => getProductMillSetup(productSizeId!),
		enabled: productSizeId != null,
	});

export const useRollsByStand = (productSizeId: number | null) =>
	useQuery({
		queryKey: MILL_SETUP_SHEET_QUERY_KEYS.rollsByStand(productSizeId),
		queryFn: () => getRollsByStand(productSizeId!),
		enabled: productSizeId != null,
	});

interface MutationCallbacks {
	onSuccess?: () => void;
	onError?: (err: Error) => void;
}

export const useUpsertMillSetupSheet = ({
	onSuccess,
	onError,
}: MutationCallbacks = {}) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: upsertMillSetupSheet,
		onSuccess: (_data, variables) => {
			// Prefix match invalidates both the proxy and wip sheet caches.
			queryClient.invalidateQueries({
				queryKey: ["millSetupSheet", "sheet", variables.productSizeId],
			});
			onSuccess?.();
		},
		onError: (err: Error) => onError?.(err),
	});
};
